import { useState, useEffect, useCallback } from 'react';
import { flujoCajaApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from '../../hooks/use-toast';
import { Calendar, Filter, ArrowDownLeft, ArrowUpRight } from 'lucide-react';

const formatCLP = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

export default function VencimientosPanel() {
  const [loading, setLoading] = useState(true);
  const [vencimientos, setVencimientos] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flujoCajaApi.getVencimientos(filtroTipo);
      setVencimientos(res.data.vencimientos || []);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error cargando vencimientos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filtroTipo]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().split('T')[0];
  const totalPorCobrar = vencimientos.filter(v => v.tipo === 'Venta').reduce((a, v) => a + (v.saldo || 0), 0);
  const totalPorPagar = vencimientos.filter(v => v.tipo === 'Compra').reduce((a, v) => a + (v.saldo || 0), 0);

  return (
    <Card data-testid="vencimientos-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="w-4 h-4" /> Vencimientos Pendientes
            </CardTitle>
            <CardDescription>Facturas de venta y compra con saldo pendiente</CardDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            {['', 'venta', 'compra'].map(t => (
              <Button
                key={t || 'all'}
                variant={filtroTipo === t ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7 px-2.5"
                onClick={() => setFiltroTipo(t)}
                data-testid={`filter-${t || 'todos'}`}
              >
                {t === '' ? 'Todos' : t === 'venta' ? 'Ventas' : 'Compras'}
              </Button>
            ))}
          </div>
        </div>
        {/* Totales */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="p-2 rounded-md border bg-blue-50 dark:bg-blue-950/20 text-xs flex justify-between">
            <span className="text-blue-900 dark:text-blue-200 flex items-center gap-1"><ArrowDownLeft className="w-3 h-3" /> Por cobrar</span>
            <span className="font-bold text-blue-700 dark:text-blue-300 tabular-nums">{formatCLP(totalPorCobrar)}</span>
          </div>
          <div className="p-2 rounded-md border bg-red-50 dark:bg-red-950/20 text-xs flex justify-between">
            <span className="text-red-900 dark:text-red-200 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> Por pagar</span>
            <span className="font-bold text-red-700 dark:text-red-300 tabular-nums">{formatCLP(totalPorPagar)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="rounded-md border overflow-hidden max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[100px]">Vencimiento</TableHead>
                  <TableHead className="w-[80px]">Tipo</TableHead>
                  <TableHead>Entidad</TableHead>
                  <TableHead className="w-[90px]">Folio</TableHead>
                  <TableHead className="text-right w-[120px]">Total</TableHead>
                  <TableHead className="text-right w-[120px]">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vencimientos.map((v) => {
                  const isVenta = v.tipo === 'Venta';
                  const isOverdue = v.fecha_vencimiento && v.fecha_vencimiento < today;
                  return (
                    <TableRow key={`${v.tipo}-${v.folio}`} className={`hover:bg-muted/20 ${isOverdue ? 'bg-red-50/50 dark:bg-red-900/5' : ''}`} data-testid={`venc-row-${v.folio}`}>
                      <TableCell className="text-xs tabular-nums">
                        {v.fecha_vencimiento}
                        {isOverdue && <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">Vencido</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={isVenta ? 'default' : 'secondary'} className="flex w-fit items-center gap-1 text-xs">
                          {isVenta ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                          {v.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-xs max-w-[200px] truncate">{v.entidad}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{v.folio}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{formatCLP(v.monto_total)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums font-semibold">
                        <span className={isVenta ? 'text-blue-700' : 'text-red-600'}>{formatCLP(v.saldo)}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {vencimientos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="p-8 text-center text-muted-foreground">
                      No hay vencimientos pendientes
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
